import { AutoCompleteTextInput } from "components/Form/AutoCompleteTextInput";
import { Form } from "components/Form/base/Form";
import { InFormLogin } from "components/Form/InFormLogin";
import { SelectInput } from "components/Form/SelectInput";
import { WeekdayInput } from "components/Form/WeekdayInput";
import FormFields from "../../Formular/FormFields";
import useFetchFields from "components/Formular/hooks/useFetchFields";
import Loader from "components/Loader";
import { usePaymentHandler } from "components/MollieConnect/hooks";
import Flex from "components/styledComponents/Flex";
import { timeDivisions } from "components/TrainingPlan/TrainingPlanHelpers";
import { useApi } from "../../../hooks/useApi";
import { useModal } from "hooks/useModal";
import Joi from "joi";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { AppState } from "redux/store";
import useSWR from "swr";
import { SubmitWithPriceCalculator } from "../SpaceBooking";
import { BookingConfirmationMessage } from "../SpaceBooking/BookingConfirmationMessage";
import { Space, SpaceBooking, SpaceSubscription } from "../types";

interface Props {
    subscription: SpaceSubscription;
    callback?: (b: SpaceBooking) => void;
}

export const SpaceSubscriptionBookingForm = ({
                                                 subscription, callback = () => {
    }
                                             }: Props) => {
    const api = useApi();
    const [setModal] = useModal();
    const [calculatedPrice, setCalculatedPrice] = useState(null);
    const isAuthenticated = useSelector((state: AppState) => state.auth.isAuthenticated);
    const { data, error } = useSWR<{ data: Space[] }>([
        `space/${subscription.club_slug}`,
        { subscription_id: subscription.id }
    ]);
    const spaceIsLoading = !data && !error;
    const [isLoading, setIsLoading] = useState(false);
    const { redirectToCheckout, preparePopup, handleError } = usePaymentHandler();
    const { fields, isLoading: formIsLoading, validationSchema } = useFetchFields(subscription?.form_id);

    const startTime = subscription.start_time;
    const endTime = subscription.end_time + 1;

    const handleSubmit = (data) => {
        setIsLoading(true);
        let popupWindow = subscription.allow_online_payments && !subscription.only_requests ? preparePopup() : null;
        return api({
            url: `space/booking/${subscription.id}`,
            method: "POST",
            data: { ...data, spaces: data.spaces.map((s) => s.id) },
            onBefore: () => setIsLoading(false),
            onError: () => handleError(popupWindow),
            onSuccess: (data) => {
                redirectToCheckout({
                    ...data,
                    popupWindow,
                    callback: () => {
                        callback(data);
                        setModal({
                            title: `Deine ${subscription.only_requests ? "Anfrage" : "Buchung"} war erfolgreich!`,
                            content: <BookingConfirmationMessage booking={data} />,
                            show: true
                        });
                    }
                });
            }
        });
    };

    const schema = Joi.object({
        start_time: Joi.number().required().min(subscription.start_time).label("Startzeit"),
        end_time: Joi.number()
            .required()
            .max(endTime)
            .min(Joi.ref("start_time"))
            .label("Endzeit")
            .messages({ "any.ref": "Startzeit muss ausgefüllt und kleiner als Endzeit sein." }),
        weekday: Joi.number().required().min(0).max(7).label("Wochentag"),
        spaces: Joi.array()
            .min(1)
            .max(1)
            .items(Joi.object({ id: Joi.number().required().min(0) }).unknown())
            .label("Platz"),
        form_data: validationSchema
    }).unknown();

    return (
        <Loader isLoading={formIsLoading || spaceIsLoading}>
            <Form onSubmit={handleSubmit} validationSchema={schema}>
                <Flex equal gap={0.5} className="mb-3">
                    <SelectInput
                        name="start_time"
                        label="Startzeit *"
                        optionsWithKey={timeDivisions.times
                            .slice(startTime, endTime)
                            .map((time, index) => ({ value: index + subscription.start_time, label: time }))}
                    />
                    <SelectInput
                        name="end_time"
                        label="Endzeit *"
                        optionsWithKey={timeDivisions.times
                            .slice(startTime, endTime)
                            .map((time, index) => ({ value: index + subscription.start_time, label: time }))}
                    />
                </Flex>
                <WeekdayInput name="weekday" label="Wochentag *" single />
                <AutoCompleteTextInput
                    emptyLabel="Keine Plätze verfügbar."
                    data={data?.data}
                    dataKey={(item: Space) => `${item.name} - ${item.space_group.name}`}
                    name="spaces"
                    label="Platz *"
                />
                {!isAuthenticated && <InFormLogin />}
                {fields && (
                    <>
                        <hr className="my-4" />
                        <FormFields fields={fields} namePrefix="form_data" />
                    </>
                )}
                <SubmitWithPriceCalculator
                    price={calculatedPrice}
                    setPrice={setCalculatedPrice}
                    isLoading={isLoading}
                    spaceSubscriptionId={subscription.id}
                >
                    Termin verbindlich {subscription.only_requests ? "anfragen" : "buchen"}
                </SubmitWithPriceCalculator>
            </Form>
        </Loader>
    );
};
